import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { UnsavedChangesDialog } from '../../components/ui/unsaved-changes-dialog';
import { useFormUnsavedChanges } from '../../hooks/use-unsaved-changes';
import { useUIStore } from '../../stores/ui-store';
import {
  EquipmentFormData,
  EquipmentCategory,
  getEquipmentCategoryName,
  PlatformEquipmentDetailResponse,
  PlatformEquipmentSummaryResponse
} from './types';
import {
  useCreatePlatformEquipment,
  useUpdatePlatformEquipment
  // usePlatformEquipmentCategories // Available for future use
} from './api';

interface EquipmentFormProps {
  isOpen: boolean;
  onClose: () => void;
  equipment?: PlatformEquipmentDetailResponse | PlatformEquipmentSummaryResponse;
  mode: 'create' | 'edit';
}

const defaultFormData: EquipmentFormData = {
  name: '',
  description: '',
  category: EquipmentCategory.LargeApparatus,
  requiresSetup: false,
  displayOrder: 1,
  iconName: '',
  isActive: true,
};

export function EquipmentForm({ isOpen, onClose, equipment, mode }: EquipmentFormProps) {
  const [formData, setFormData] = useState<EquipmentFormData>(defaultFormData);
  const [initialFormData, setInitialFormData] = useState<EquipmentFormData>(defaultFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const { addNotification } = useUIStore();
  const createMutation = useCreatePlatformEquipment();
  const updateMutation = useUpdatePlatformEquipment();
  // const { data: categories } = usePlatformEquipmentCategories(); // Available for future use

  // Reset form when modal opens/closes or equipment changes
  useEffect(() => {
    if (isOpen) {
      let newFormData;
      if (mode === 'edit' && equipment) {
        newFormData = {
          name: equipment.name,
          description: equipment.description || '',
          category: equipment.category,
          requiresSetup: equipment.requiresSetup,
          displayOrder: equipment.displayOrder,
          iconName: equipment.iconName || '',
          isActive: equipment.isActive,
        };
      } else {
        newFormData = defaultFormData;
      }
      setFormData(newFormData);
      setInitialFormData(newFormData);
      setErrors({});
    }
  }, [isOpen, mode, equipment]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Equipment name is required';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveForm = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (mode === 'create') {
        await createMutation.mutateAsync({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          category: formData.category,
          requiresSetup: formData.requiresSetup,
          iconName: formData.iconName.trim() || undefined,
          displayOrder: 0, // Default display order for new equipment
        });

        addNotification({
          type: 'success',
          title: 'Equipment Created',
          message: `${formData.name} has been created successfully.`,
        });
      } else {
        if (!equipment) return;

        await updateMutation.mutateAsync({
          id: equipment.id,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          category: formData.category,
          requiresSetup: formData.requiresSetup,
          iconName: formData.iconName.trim() || undefined,
          isActive: formData.isActive ?? true,
          displayOrder: equipment.displayOrder ?? 0, // Preserve existing display order
        });

        addNotification({
          type: 'success',
          title: 'Equipment Updated',
          message: `${formData.name} has been updated successfully.`,
        });
      }

      onClose();
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: `Failed to ${mode === 'create' ? 'Create' : 'Update'} Equipment`,
        message: error.message || 'An unexpected error occurred.',
      });
      throw error; // Re-throw for unsaved changes handler
    }
  };

  // Unsaved changes detection
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _unsavedChanges = useFormUnsavedChanges(initialFormData, formData, {
    enablePageUnload: isOpen, // Only warn about page unload when form is open
    enableNavigation: false, // We'll handle navigation manually for modals
    onSave: saveForm,
    onDiscard: () => {
      setFormData(initialFormData);
      onClose();
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveForm();
  };

  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  const handleClose = () => {
    if (JSON.stringify(initialFormData) !== JSON.stringify(formData)) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  };

  const handleInputChange = (field: keyof EquipmentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>
            {mode === 'create' ? 'Create New Equipment' : 'Edit Equipment'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={errors.name ? 'border-red-500' : ''}
                placeholder="Enter equipment name"
                disabled={isLoading}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Enter equipment description"
                disabled={isLoading}
              />
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => handleInputChange('category', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                {Object.values(EquipmentCategory)
                  .filter(value => typeof value === 'number')
                  .map((category) => (
                    <option key={category} value={category}>
                      {getEquipmentCategoryName(category as EquipmentCategory)}
                    </option>
                  ))}
              </select>
            </div>


            {/* Icon Name */}
            <div>
              <label htmlFor="iconName" className="block text-sm font-medium text-gray-700 mb-1">
                Icon Name
              </label>
              <Input
                id="iconName"
                type="text"
                value={formData.iconName}
                onChange={(e) => handleInputChange('iconName', e.target.value)}
                placeholder="e.g., reformer, mat, chair"
                disabled={isLoading}
              />
            </div>

            {/* Requires Setup */}
            <div className="flex items-center space-x-2">
              <input
                id="requiresSetup"
                type="checkbox"
                checked={formData.requiresSetup}
                onChange={(e) => handleInputChange('requiresSetup', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={isLoading}
              />
              <label htmlFor="requiresSetup" className="text-sm font-medium text-gray-700">
                Requires Setup
              </label>
            </div>

            {/* Is Active (only for edit mode) */}
            {mode === 'edit' && (
              <div className="flex items-center space-x-2">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={isLoading}
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  Active
                </label>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="min-w-[80px]"
              >
                {isLoading ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={showUnsavedDialog}
        title="Unsaved Changes"
        message="You have unsaved changes. What would you like to do?"
        onSave={async () => {
          try {
            await saveForm();
            setShowUnsavedDialog(false);
          } catch (error) {
            // Error is already handled in saveForm
          }
        }}
        onDiscard={() => {
          setFormData(initialFormData);
          setShowUnsavedDialog(false);
          onClose();
        }}
        onCancel={() => {
          setShowUnsavedDialog(false);
        }}
        saveLabel={mode === 'create' ? 'Save & Close' : 'Update & Close'}
        discardLabel="Discard Changes"
      />
    </div>
  );
}